import moment from "moment";
// import { useSelector } from "react-redux";
import { ORDER_TYPES } from "./Constants";
import {
  convertToMoment,
  getTimeIntervals,
  getTodayTimeIntervals,
  isRunningHoursMidnightPattern,
} from "./TimeUtils";
import { isTimeInClosure } from "./ClosureUtils";
import { useDispatch } from "react-redux";
import { setIsTodayHoliday } from "../store/Restaurant/RestaurantActions";

export const getViewPortHeight = () => {
  const { innerHeight: height } = window;
  return height;
};

// Normalize a user-typed phone number to E.164-ish form: keep a single
// leading "+" (if present) and strip every other non-digit character
// (spaces, dashes, parentheses, dots). Firebase phone auth
// (signInWithPhoneNumber) and the order API expect this format — a valid
// number typed with separators like "+64 210 889 6192" would otherwise be
// rejected. Returns the value untouched when empty/nullish.
export const sanitizePhoneNumber = (phone) => {
  if (!phone) return phone;
  const trimmed = String(phone).trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
};

export const setLocalStorageItem = (name, val) => {
  localStorage.setItem(name, val);
};

export const getLocalStorageItem = (name) => {
  return localStorage.getItem(name);
};

export const deleteLocalStorageItem = (name) => {
  localStorage.removeItem(name);
};

export const clearEntireLocalStorage = () => {
  localStorage.clear();
};

export const modifySlotsByType = (
  hotel,
  cart,
  formValues,
  res,
  deliveryTime,
  closures = []
) => {
  // Don't short-circuit on an empty `res`: when the shop is in its final
  // buffer-minutes (e.g. 8:50 PM with a 8:55 PM close and 15-min buffer),
  // the grid generator returns no SCHEDULED slots, but ASAP should still
  // be available because `now` is still inside operating hours. The
  // result of this function gates the ASAP radio enable (via
  // `getLabel(slots)`), so an early return here disables ASAP exactly
  // when the user needs it most. Continue and let the ASAP-applicability
  // branch decide whether to add an ASAP entry.
  if (!deliveryTime) {
    return [];
  }

  const currentSelectedTime = moment(deliveryTime);
  const result = [];
  const dayInfo = hotel.workingInfo.find((elem) => elem.day === moment().day());
  const hours = dayInfo?.hotelDayInfo.find(
    (elem) => elem.type === formValues.orderType
  );

  // Decide whether an ASAP entry should be added for today's view. ASAP
  // is only meaningful when the selected delivery date is today AND `now`
  // is inside an active running-hours window AND the requested channel
  // is offered by the shop. We deliberately do NOT gate on the backend's
  // `openingAvailable` / `pickupAvailable` / `deliveryAvailable` flags
  // here: those are buffer-adjusted server-side ("can the kitchen prep
  // an order placed *now*?"), so they go false in the final buffer-many
  // minutes before close — at e.g. 8:27 PM with a shop closing 8:30 PM
  // and a 15-min buffer, the backend reports `openingAvailable=false`.
  // The user-facing rule is "ASAP available until last end time", so we
  // gate on the running-hours window plus the *enabled* channel flag
  // (`hotel.pickup`/`hotel.delivery` — i.e. is this channel offered at
  // all). `isAsapAvailable` in useDynamicHotelData uses the same
  // running-hours-only gate so the radio's enabled/disabled stays in
  // sync with the slot list.
  let isAsapApplicable = false;
  // Skip ASAP creation entirely when `now` is inside an active closure
  // window. This mirrors `isAsapAvailable`'s closure gate in the modal
  // — without it, the slot-list builder emits an ASAP entry that
  // survives `filterSlotsByClosures` (ASAP entries are exempt from
  // closure filtering since their value is just an estimated-ready-
  // time hint). In the modal that's harmless because the ASAP radio's
  // `disabled` also gates on `isAsapAvailable` (which IS closure-aware)
  // → radio shows "Unavailable". But the checkout page has no radio;
  // it picks ASAP via the TimePicker dropdown, which surfaces whatever
  // is in the slot list — so an ASAP entry emitted during a closure
  // makes ASAP selectable when it shouldn't be.
  const nowMs = moment().valueOf();
  const nowInClosure = isTimeInClosure(nowMs, closures);
  if (moment().isSame(currentSelectedTime, "date") && !nowInClosure) {
    hours?.running_hours.forEach((h) => {
      const start = convertToMoment(h.start);
      const stop = convertToMoment(h.stop);
      if (
        start &&
        start.isValid() &&
        stop &&
        stop.isValid() &&
        moment().isBetween(start, stop)
      ) {
        if (
          formValues?.orderType === ORDER_TYPES.DELIVERY &&
          hotel?.delivery
        ) {
          isAsapApplicable = true;
        } else if (
          formValues?.orderType === ORDER_TYPES.PICKUP &&
          hotel?.pickup
        ) {
          isAsapApplicable = true;
        }
      }
    });
  }

  let asapValue = null;
  if (isAsapApplicable) {
    const type = cart?.checkoutDetails.orderType;
    let buffer;
    // Buffer is keyed off the *enabled* channel flag for the same reason
    // as the gate above — `hotel.pickupAvailable` can flip false in the
    // final-buffer-minutes window even though the shop still offers
    // pickup as a channel. We just need the prep-time hint.
    if (type === ORDER_TYPES.DELIVERY && hotel?.delivery) {
      buffer = hotel.delivery_buffer_time;
    } else if (type === ORDER_TYPES.PICKUP && hotel?.pickup) {
      buffer = hotel.pickup_buffer_time;
    }
    if (!buffer) {
      buffer = 10;
    }
    const showASAP = buffer + "-" + (buffer + 5) + " Mins";
    // ASAP value is anchored to `now + buffer` (not a grid time) so the
    // checkout payload's `setOrderType` resolves back to "ASAP" instead
    // of "SCHEDULED" for a slot whose value is far in the future.
    asapValue = moment().add(buffer, "minutes").format("hh:mm a");
    result.push({
      typeOfOrder: "ASAP",
      value: asapValue,
      label: showASAP,
    });
  }

  // All running-hours grid slots are SCHEDULED. Previously res[0] was
  // consumed to back the ASAP entry, which meant a shop with only one
  // remaining grid slot (e.g. just "07:00 PM" at 6:46) would lose its
  // SCHEDULED list entirely once ASAP fired — the user could no longer
  // book the end-of-day slot. Keeping res entries intact ensures the
  // SCHEDULED dropdown always reflects the actual running-hours grid.
  // Skip the res entry whose value collides with the ASAP value (happens
  // when `now + buffer` lands exactly on a grid time, e.g. shop opens
  // 3:00 PM, buffer 15 min — both ASAP and res[0] would render as
  // "03:15 pm", giving Material-UI's Select two MenuItems with the same
  // value).
  for (let i = 0; i < res.length; i++) {
    if (asapValue && res[i] === asapValue) continue;
    result.push({
      typeOfOrder: "SCHEDULED",
      value: res[i],
      label: res[i],
    });
  }
  return result;
};

export const generateDateSlots = (hotel, orderType) => {
  const dates = [];
  let isToday = true;
  let pre_min_day = hotel?.pre_min_day === (null || 0) ? 1 : hotel?.pre_min_day;

  for (let i = 0; i < pre_min_day; i++) {
    const validDay = hotel?.workingInfo.filter(
      (d) => d.day === moment().add(i, "day").day()
    );
    const validDayInfo = validDay[0]?.hotelDayInfo?.filter(
      (d) => d.type === orderType
    );
    let isTodayOpen = true;
    let isHoliday = false;
    if (
      isToday &&
      validDayInfo &&
      moment().add(i, "day").day() === moment().day()
    ) {
      isToday = false;
      // Today appears in the date picker as long as the shop hasn't
      // fully closed for the day — i.e. there's at least one
      // running_hours block whose `stop` is still in the future. The
      // buffer is intentionally NOT applied here: ASAP must be
      // available all the way up to the close time (rule: "user can
      // order ASAP till last end time"), so dropping today from the
      // date picker just because `now + buffer >= stop` would lock
      // users out of placing a final-minutes ASAP order from the
      // checkout page even though the shop is still operating.
      let hasAvailableSlot = false;
      for (let j = 0; j < validDayInfo[0]?.running_hours?.length; j++) {
        const stop = convertToMoment(validDayInfo[0]?.running_hours[j]?.stop);
        if (moment().isBefore(stop)) {
          hasAvailableSlot = true;
          break;
        }
      }
      isTodayOpen = hasAvailableSlot;
    }

    if (hotel?.shop_hoilidays?.length > 0) {
      for (let h = 0; h < hotel?.shop_hoilidays?.length; h++) {
        if (
          moment().add(i, "day").format("YYYY-MM-DD") ===
          hotel.shop_hoilidays[h].date
        ) {
          isHoliday = true;
          // dispatch(setIsTodayHoliday(true));
          break;
        }
      }
    }

    // Midnight-store carry-over: even when day i is a holiday, include it
    // in the picker if the shop's pattern wraps midnight AND the previous
    // day is operational. The overnight tail of the previous day's shift
    // (e.g. 12am–3am) still operates on the holiday morning. Slot
    // generation will filter the holiday day to only that overnight slot.
    let isOvernightExtensionDay = false;
    if (
      isHoliday &&
      i > 0 &&
      validDayInfo &&
      validDayInfo.length > 0 &&
      validDayInfo[0]?.running_hours?.length > 0 &&
      isRunningHoursMidnightPattern(validDayInfo[0].running_hours)
    ) {
      const prevDate = moment().add(i - 1, "day");
      const prevDayInfo = hotel?.workingInfo?.find(
        (d) => d.day === prevDate.day()
      );
      const prevHours = prevDayInfo?.hotelDayInfo?.find(
        (d) => d.type === orderType
      )?.running_hours;
      const prevIsHoliday = (hotel?.shop_hoilidays || []).some(
        (h) => h?.date === prevDate.format("YYYY-MM-DD")
      );
      const prevOperational =
        !!prevDayInfo &&
        prevDayInfo.is_closed !== 1 &&
        !prevIsHoliday &&
        Array.isArray(prevHours) &&
        prevHours.length > 0;
      if (prevOperational) {
        isOvernightExtensionDay = true;
      }
    }

    if (
      !validDayInfo ||
      validDayInfo.length === 0 ||
      !isTodayOpen ||
      (isHoliday && !isOvernightExtensionDay)
    ) {
      continue;
    }

    let label = moment().add(i, "day").format("dddd");
    if (moment().add(i, "day").format("LL") === moment().format("LL")) {
      label = "Today";
    } else if (
      moment().add(i, "day").format("LL") ===
      moment().add(1, "day").format("LL")
    ) {
      label = "Tomorrow";
    }

    const date = {
      date: moment().add(i, "day").format("LL"),
      label,
    };
    dates.push(date);
  }
  //   }

  // }
  // console.log("datesssssssssss", dates);
  return dates;
};

export const getTimeIntervalSlots = (hotel, cart, formValues, deliveryTime) => {
  if (!hotel || !deliveryTime) {
    return [];
  }

  const bufTime =
    formValues.orderType === "DELIVERY"
      ? hotel.delivery_buffer_time
      : hotel.pickup_buffer_time;
  // console.log("hotel infoooo firstttttt", hotel.workingInfo)
  const dayInfo = hotel.workingInfo.find(
    (elem) => elem.day === moment(deliveryTime).day()
  );
  // console.log("hotelll", hotel)
  // console.log("day infoooo", dayInfo)
  // console.log("hotel day infoooo", dayInfo?.hotelDayInfo)
  if (dayInfo && dayInfo.hotelDayInfo) {
    const hours = dayInfo.hotelDayInfo.find(
      (elem) => elem.type === formValues.orderType
    );
    const data = hours?.running_hours.map((elem) => {
      // const options = getTodayTimeIntervals(elem.start, elem.stop, 15, bufTime);
      const options = getTimeIntervals(elem.start, elem.stop, 15, bufTime);

      return options;
    });

    const sorted = data.sort((a, b) => {
      const timeA = moment(a, "HH:mm");
      const timeB = moment(b, "HH:mm");

      return timeA.isBefore(timeB) ? -1 : 1;
    });
    const res = sorted ? sorted.reduce((prev, curr) => [...curr, ...prev]) : [];
    // const res = data ? data : [];

    const result = modifySlotsByType(
      hotel,
      cart,
      formValues,
      res,
      deliveryTime
    );
    return result;
  } else {
    return [];
  }
};
